import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";

/** Source icon: Reddit (same as Dashboard) */
function RedditSourceIcon({ className = "h-4 w-4" }) {
  return (
    <FontAwesomeIcon icon={faReddit} className={`shrink-0 text-[#ff4500] ${className}`} />
  );
}

/** Source icon: LinkedIn (same as Dashboard) */
function LinkedInSourceIcon({ className = "h-4 w-4" }) {
  return (
    <FontAwesomeIcon icon={faLinkedin} className={`shrink-0 text-[#0077b5] ${className}`} />
  );
}

/** Simple realistic faces (Random User Me). */
const AVATAR = (gender, id) =>
  `https://randomuser.me/api/portraits/${gender}/${id}.jpg`;

/**
 * Red Ocean vs Signal. Left = Upwork-style job listings. Right = Reddit/LinkedIn-style feed.
 */
export function SplitContrastCard() {
  return (
    <div className="relative w-full max-w-6xl mx-auto grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12 items-start">
      {/* LEFT: Upwork-style job board */}
      <div className="relative rounded-3xl border border-slate-200 bg-slate-100 p-8 overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
          <div className="absolute top-10 left-6 w-40 h-40 rounded-full bg-red-200 blur-3xl" />
          <div className="absolute bottom-10 right-6 w-40 h-40 rounded-full bg-orange-200 blur-3xl" />
        </div>

        <p className="relative mb-6 text-xs font-semibold tracking-widest text-slate-500">
          TRADITIONAL FREELANCE PLATFORMS
        </p>

        {/* Upwork-style Job Card 1 */}
        <div className="relative mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src={AVATAR("men", 1)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
              />
              <h4 className="text-sm font-semibold text-slate-900 truncate">
                Build a SaaS landing page (Webflow)
              </h4>
            </div>
            <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              Fixed
            </span>
          </div>
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-600">
            Need a clean landing page for our B2B product. Must have Webflow experience…
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span><strong className="text-slate-700">$800</strong> budget</span>
            <span>Posted 2 hours ago</span>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-red-600">Proposals: 47</span>
            <div className="flex -space-x-1.5">
              {[1, 2, 3, 4].map((i) => (
                <img
                  key={i}
                  src={AVATAR("men", 2 + i)}
                  alt=""
                  className="h-5 w-5 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Upwork-style Job Card 2 */}
        <div className="relative mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src={AVATAR("women", 1)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
              />
              <h4 className="text-sm font-semibold text-slate-900 truncate">
                Email automation setup (Zapier/Make)
              </h4>
            </div>
            <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              Hourly
            </span>
          </div>
          <p className="mt-1.5 line-clamp-2 text-xs text-slate-600">
            Looking for someone to connect our CRM to email sequences…
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span><strong className="text-slate-700">$25–50/hr</strong></span>
            <span>Posted 5 hours ago</span>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-red-600">Proposals: 32</span>
            <div className="flex -space-x-1.5">
              {[11, 12, 13, 14].map((i) => (
                <img
                  key={i}
                  src={AVATAR("women", i)}
                  alt=""
                  className="h-5 w-5 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
          </div>
        </div>

        <p className="relative mt-6 text-sm text-slate-500">
          Compete. Bid. Undercut. Wait.
        </p>
      </div>

      {/* RIGHT: Reddit / LinkedIn-style feed */}
      <div className="relative rounded-3xl border border-emerald-200 bg-white p-8 shadow-xl">
        <div className="absolute -z-10 inset-0 rounded-3xl bg-gradient-to-br from-emerald-100/40 to-green-100/30 blur-3xl" aria-hidden />

        <div className="mb-5 flex items-center justify-between">
          <p className="text-xs font-semibold tracking-widest text-emerald-600">
            BUYER CONVERSATIONS
          </p>
          <div className="flex items-center gap-2 text-xs text-emerald-600">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </div>
        </div>

        {/* Reddit-style post 1 */}
        <div className="mb-4 rounded-lg border border-emerald-200 bg-white p-4 flex gap-3">
          <img
            src={AVATAR("men", 10)}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <RedditSourceIcon className="h-3.5 w-3.5" />
              <span>r/forhire</span>
              <span>·</span>
              <span>6 min ago</span>
            </div>
            <p className="text-sm font-medium text-slate-900">
              Looking for someone to build a SaaS landing page – any Webflow devs here?
            </p>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              <span>12 upvotes</span>
              <span>3 comments</span>
              <span className="text-emerald-600 font-medium">No proposals yet</span>
            </div>
          </div>
        </div>

        {/* LinkedIn-style post 2 */}
        <div className="mb-6 rounded-lg border border-emerald-200 bg-white p-4 flex gap-3">
          <img
            src={AVATAR("women", 10)}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <LinkedInSourceIcon className="h-3.5 w-3.5" />
              <span>LinkedIn</span>
              <span>·</span>
              <span>14 min ago</span>
            </div>
            <p className="text-sm font-medium text-slate-900">
              Anyone good with email automation? We need Zapier/Make help for our CRM.
            </p>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
              <span>Direct conversation</span>
              <span className="text-emerald-600 font-medium">First to reply wins</span>
            </div>
          </div>
        </div>

        {/* Intent signal */}
        <div className="mb-5">
          <div className="mb-1.5 flex justify-between text-xs text-slate-600">
            <span>Intent signal</span>
            <span className="text-emerald-600 font-medium">Strong</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-emerald-100">
            <div className="h-1.5 w-[85%] rounded-full bg-emerald-500" />
          </div>
        </div>

        <p className="text-sm font-medium text-emerald-700">
          Respond first. Build trust. Close faster.
        </p>
      </div>
    </div>
  );
}

/** Simple arrow connector: dotted line + arrowhead. Stays aligned at any viewport. */
function FlowArrow({ className = "" }) {
  return (
    <div className={`flex shrink-0 items-center justify-center text-emerald-500 ${className}`} aria-hidden>
      <svg width="32" height="24" viewBox="0 0 32 24" fill="none" className="w-8 h-6 md:w-10 md:h-7">
        <line x1="0" y1="12" x2="24" y2="12" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" strokeLinecap="round" />
        <path d="M24 8v8l6-4-6-4z" fill="currentColor" />
      </svg>
    </div>
  );
}

/**
 * Signal Convergence Engine: Reddit + LinkedIn → LeadZLab AI → Qualified Leads.
 * Simple flex layout with arrow connectors; works at all screen sizes.
 */
export function SignalConvergenceEngine() {
  return (
    <div className="w-full max-w-full mx-auto py-6 md:py-8">
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-2">
        {/* 1. Sources */}
        <div className="flex flex-row justify-center gap-4 md:flex-col md:gap-2 md:shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-100">
              <RedditSourceIcon className="h-4 w-4 text-[#ff4500]" />
            </div>
            <span className="text-sm font-medium text-slate-800">Reddit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100">
              <LinkedInSourceIcon className="h-4 w-4 text-[#0077b5]" />
            </div>
            <span className="text-sm font-medium text-slate-800">LinkedIn</span>
          </div>
        </div>

        <FlowArrow className="rotate-90 md:rotate-0 shrink-0 md:w-6" />

        {/* 2. AI Engine */}
        <div className="flex justify-center shrink-0">
          <div className="rounded-xl border-2 border-emerald-400 bg-white px-4 py-4 text-center shadow-md md:px-5 md:py-5">
            <p className="text-[10px] font-semibold tracking-widest text-emerald-600 mb-0.5">LEADZLAB</p>
            <h3 className="text-base font-semibold text-slate-900">AI Signal Engine</h3>
            <p className="mt-1 text-[11px] text-slate-500">Filters · Scores · Routes</p>
          </div>
        </div>

        <FlowArrow className="rotate-90 md:rotate-0 shrink-0 md:w-6" />

        {/* 3. Example leads */}
        <div className="flex flex-col gap-2 min-w-0 flex-1 md:flex-initial md:max-w-[180px]">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 shadow-sm">
            <div className="flex items-start gap-2">
              <img
                src={AVATAR("men", 60)}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover border border-emerald-200"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-slate-500 mb-0.5">
                  <RedditSourceIcon className="h-3 w-3 shrink-0" />
                  <span className="text-[11px]">r/forhire</span>
                </div>
                <p className="font-semibold text-slate-900 text-xs leading-snug">SaaS Landing Page Project</p>
                <p className="text-[11px] text-slate-500 mt-0.5">High intent · 6 min ago</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 shadow-sm">
            <div className="flex items-start gap-2">
              <img
                src={AVATAR("women", 65)}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover border border-emerald-200"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-slate-500 mb-0.5">
                  <LinkedInSourceIcon className="h-3 w-3 shrink-0" />
                  <span className="text-[11px]">LinkedIn</span>
                </div>
                <p className="font-semibold text-slate-900 text-xs leading-snug">Email Automation ($1k+)</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Budget confirmed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Product-style: criteria widget (skills + subreddits + LinkedIn) */
export function CriteriaWidget() {
  return (
    <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Your skills
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
          Webflow
        </span>
        <span className="rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
          Email automation
        </span>
        <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          r/forhire
        </span>
        <span className="rounded-md bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
          LinkedIn
        </span>
      </div>
      <div className="mt-3 border-t border-slate-100 pt-3">
        <span className="inline-block rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white">
          Get conversations
        </span>
      </div>
    </div>
  );
}

/** Product-style: one-feed widget (Reddit + LinkedIn) */
export function OneFeedWidget() {
  return (
    <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        One feed
      </p>
      <div className="mt-3 space-y-3">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-medium text-slate-800 line-clamp-2">
            &ldquo;Hiring: Full-stack dev for our MVP, $5k budget&rdquo;
          </p>
          <p className="mt-1.5 text-xs text-slate-500">r/forhire · 2h ago</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-medium text-slate-800 line-clamp-2">
            &ldquo;Looking for branding help—logo + brand guidelines&rdquo;
          </p>
          <p className="mt-1.5 text-xs text-slate-500">LinkedIn · 1h ago</p>
        </div>
      </div>
    </div>
  );
}

/** Freelance-focused: live client opportunities — “While You Work” (not pipeline/enterprise) */
export function LiveClientFeed() {
  return (
    <div className="relative w-full max-w-[520px] rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold tracking-wide text-slate-500">
            LIVE CLIENT OPPORTUNITIES
          </p>
          <h3 className="text-lg font-semibold text-slate-900">While You Work</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-600">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          Active
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-3">
        <img
          src={AVATAR("men", 15)}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">
            &ldquo;Looking for a freelance Webflow dev for a SaaS landing page&rdquo;
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <RedditSourceIcon className="h-3.5 w-3.5" />
              r/forhire
            </span>
            <span>4 min ago</span>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-3">
        <img
          src={AVATAR("women", 15)}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover border border-slate-200"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">
            &ldquo;Need help setting up email automation – budget $1k+&rdquo;
          </p>
          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <LinkedInSourceIcon className="h-3.5 w-3.5" />
              LinkedIn
            </span>
            <span>12 min ago</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 p-4">
        <div>
          <p className="text-xs text-slate-500">Matches your skills</p>
          <p className="text-sm font-semibold text-emerald-700">
            Ready to Reach Out
          </p>
        </div>
        <span className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-black">
          Message
        </span>
      </div>
    </div>
  );
}

/** Product-style: single thread card for use-case (source = subreddit e.g. r/SaaS or "LinkedIn") */
export function ThreadCard({ quote, subreddit, source, time, tag }) {
  const sourceLabel = source ?? subreddit;
  const isLinkedIn = source === "LinkedIn";
  const SourceIcon = isLinkedIn ? LinkedInSourceIcon : RedditSourceIcon;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs font-medium text-slate-900 line-clamp-2">&ldquo;{quote}&rdquo;</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-500">
          <SourceIcon className="h-3.5 w-3.5 shrink-0" />
          {sourceLabel}{time ? ` · ${time}` : ""}
        </span>
        {tag && (
          <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
            {tag}
          </span>
        )}
      </div>
    </div>
  );
}

/** Product-style hero: ICP-focused feed with Reddit/LinkedIn source indicators */
export function LiveLeadEngine() {
  return (
    <div className="relative w-full max-w-[520px] transition duration-500 hover:rotate-0 lg:rotate-[1deg]">
      <div className="absolute -z-10 inset-0 rounded-full bg-gradient-to-br from-emerald-100/40 to-blue-100/40 blur-3xl" aria-hidden />
      <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        {/* Top Bar — ICP framing */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold tracking-wide text-emerald-600">
              REDDIT & LINKEDIN MONITOR
            </p>
            <h3 className="text-lg font-semibold text-slate-900">
              ICP-matched leads
            </h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Live
          </div>
        </div>

        {/* Lead 1 — Reddit (source icon + r/SaaS) */}
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-900">
            &ldquo;Hiring: Full-stack developer for our MVP, $5k budget&rdquo;
          </p>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <RedditSourceIcon />
              <span>r/forhire</span>
            </span>
            <span>8 min ago</span>
          </div>
          <div className="mt-3 flex gap-2">
            <span className="rounded-md bg-emerald-100 px-2 py-1 text-xs text-emerald-700">
              Hiring
            </span>
            <span className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700">
              Lead Gen
            </span>
          </div>
        </div>

        {/* Lead 2 — LinkedIn (source icon) */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-900">
            &ldquo;Looking for branding help—logo refresh and brand guidelines for our SaaS&rdquo;
          </p>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <LinkedInSourceIcon />
              <span>LinkedIn</span>
            </span>
            <span>21 min ago</span>
          </div>
          <div className="mt-3 flex gap-2">
            <span className="rounded-md bg-emerald-100 px-2 py-1 text-xs text-emerald-700">
              Seeking
            </span>
            <span className="rounded-md bg-purple-100 px-2 py-1 text-xs text-purple-700">
              Branding
            </span>
          </div>
        </div>

        {/* Bottom — ICP match → pipeline */}
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div>
            <p className="text-xs text-slate-500">Matches your ICP</p>
            <p className="text-sm font-semibold text-emerald-700">
              Added to Leads Pipeline
            </p>
          </div>
          <span className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs text-white transition hover:bg-emerald-700">
            View Lead
          </span>
        </div>
      </div>
    </div>
  );
}
